import { useCallback, useEffect, useRef, useState } from "react";
import {
  getThgAnnualSummary,
  recomputeThgAnnualSummary,
} from "../../../services/clearing/thg-annual-summary";
import type {
  ThgAnnualSummary,
  ThgFailure,
  ThgResult,
  ThgStationPayout,
} from "../../../types/thgAnnualSummary";

interface UseThgAnnualSummaryArgs {
  initialYear: number;
  /** False for a user who may not see THG figures: no request is made at all. */
  enabled?: boolean;
}

/**
 * The THG summary for one selected year, plus recompute.
 *
 * Every change of year fetches afresh and nothing is cached: a recompute rewrites a final year, and owner
 * details are looked up live by the backend, so an earlier copy can be wrong. Requests are numbered so
 * that a slow response for a year the reader has already left (the first read of a final year and every
 * read of the current year take several seconds) can never overwrite the year they switched to.
 */
export const useThgAnnualSummary = ({ initialYear, enabled = true }: UseThgAnnualSummaryArgs) => {
  const [year, setYearState] = useState(initialYear);
  const [summary, setSummary] = useState<ThgAnnualSummary | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [failure, setFailure] = useState<ThgFailure | null>(null);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeFailure, setRecomputeFailure] = useState<ThgFailure | null>(null);
  const latestRequest = useRef(0);

  const load = useCallback(
    async (target: number) => {
      if (!enabled) return;
      const requestId = ++latestRequest.current;
      setSummary(null);
      setFailure(null);
      setRecomputeFailure(null);
      setLoading(true);

      const result: ThgResult = await getThgAnnualSummary(target);
      if (requestId !== latestRequest.current) return;

      if (result.success === true) setSummary(result.data);
      else setFailure(result as ThgFailure);
      setLoading(false);
    },
    [enabled],
  );

  useEffect(() => {
    load(year);
  }, [load, year]);

  const setYear = useCallback((next: number) => setYearState(next), []);

  const retry = useCallback(() => load(year), [load, year]);

  /**
   * Re-reads the shown year without clearing it, after a credit-note action (CLR-283). The table stays on
   * screen, on its page, while this runs. Returns false when it failed (the figures stay) or when the reader
   * switched year meanwhile (the result is dropped).
   */
  const refresh = useCallback(async (): Promise<boolean> => {
    if (!enabled) return false;
    const target = year;
    const requestId = ++latestRequest.current;
    const result: ThgResult = await getThgAnnualSummary(target);
    if (requestId !== latestRequest.current) return false;
    if (result.success === true) {
      setSummary(result.data);
      return true;
    }
    return false;
  }, [enabled, year]);

  /** Shows a generate response in its row at once, before the refresh brings the full row. */
  const patchStationPayout = useCallback((stationId: string, payout: ThgStationPayout) => {
    setSummary((current) =>
      current
        ? {
            ...current,
            stations: current.stations.map((station) =>
              station.stationId === stationId ? { ...station, payout } : station,
            ),
          }
        : current,
    );
  }, []);

  /** Returns whether it succeeded, so the caller can confirm it to the reader. */
  const recompute = useCallback(async (): Promise<boolean> => {
    if (!summary || summary.status !== "FINAL" || recomputing) return false;
    const requestId = latestRequest.current;
    setRecomputing(true);
    setRecomputeFailure(null);

    const result: ThgResult = await recomputeThgAnnualSummary(summary.year);
    setRecomputing(false);
    // The reader switched year while it ran: the result belongs to a year no longer on screen.
    if (requestId !== latestRequest.current) return false;

    if (result.success === true) {
      setSummary(result.data);
      return true;
    }
    setRecomputeFailure(result as ThgFailure);
    return false;
  }, [summary, recomputing]);

  return {
    year,
    setYear,
    summary,
    loading,
    failure,
    recomputing,
    recomputeFailure,
    recompute,
    retry,
    refresh,
    patchStationPayout,
  };
};
